import React, { useState } from "react";
import { ArrowLeft, RotateCcw } from "lucide-react";

import Button from "../../../../../components/buttons/Button";
import FormModal from "../../../../../components/modals/FormModal";
import {
  FormLayout,
  FormSection,
} from "../../../../../components/layout/forms/Index";

import "./PrivateTalentPulseForm.css";

export default function PrivateTalentPulseForm({
  sport,
  dataset,
  loading,
  saving,
  error,
  onSave,
  onClose,
}) {
  const [step, setStep] = useState("intro");
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState([]);

  const validDataset = dataset?.sport === sport ? dataset : null;

  const QUESTIONS = validDataset?.QUESTIONS || [];
  const ARCHETYPES = validDataset?.ARCHETYPES || {};

  const handleAnswer = (type) => {
    if (saving || !QUESTIONS[currentIdx]) return;

    const updated = [...answers, type];

    setAnswers(updated);

    if (currentIdx < QUESTIONS.length - 1) {
      setCurrentIdx(currentIdx + 1);
    } else {
      setStep("result");
    }
  };

  const handlePrevious = () => {
    if (!currentIdx || saving) return;

    setCurrentIdx((current) => current - 1);
    setAnswers((current) => current.slice(0, -1));
  };

  const handleRestart = () => {
    if (saving) return;

    setStep("intro");
    setCurrentIdx(0);
    setAnswers([]);
  };

  const counts = answers.reduce((result, type) => {
    result[type] = (result[type] || 0) + 1;
    return result;
  }, {});

  const topType = Object.keys(counts).sort(
    (a, b) => counts[b] - counts[a] || answers.indexOf(a) - answers.indexOf(b),
  )[0];

  const result = ARCHETYPES[topType] || null;

  const handleSave = async () => {
    if (!result || saving || answers.length !== QUESTIONS.length) {
      return;
    }

    await onSave(answers);
  };

  const progress = QUESTIONS.length
    ? ((currentIdx + 1) / QUESTIONS.length) * 100
    : 0;

  return (
    <FormModal isOpen title={`${sport} Talent Pulse`} onClose={onClose}>
      <div className="private-talent-pulse-form">
        <FormLayout
          title={
            step === "intro"
              ? `${sport} Pulse Assessment`
              : step === "quiz"
                ? `Question ${currentIdx + 1}`
                : "Your Pulse Result"
          }
          description={
            step === "intro"
              ? "Discover your sporting style."
              : step === "quiz"
                ? `${currentIdx + 1} of ${QUESTIONS.length} questions`
                : `Your ${sport} playing-style profile`
          }
        >
          {error && (
            <p className="private-talent-pulse-form__error" role="alert">
              {error}
            </p>
          )}

          {step === "intro" && (
            <FormSection>
              <div className="private-talent-pulse-form__intro">
                <p>
                  Complete a {QUESTIONS.length || 10}-question assessment
                  designed for {sport}.
                </p>

                {validDataset?.metrics?.length === 5 && (
                  <div className="private-talent-pulse-form__strengths">
                    {validDataset.metrics.map((metric) => (
                      <span key={metric}>{metric}</span>
                    ))}
                  </div>
                )}

                <p>
                  You can retake the assessment. Your existing result will only
                  change when you save.
                </p>

                <div className="private-talent-pulse-form__actions">
                  <Button
                    value={
                      loading ? "Loading Assessment..." : "Start Assessment"
                    }
                    disabled={loading || saving || !QUESTIONS.length}
                    onClick={() => setStep("quiz")}
                  />

                  <Button
                    value="Cancel"
                    variant="primary-outlined"
                    disabled={saving}
                    onClick={onClose}
                  />
                </div>
              </div>
            </FormSection>
          )}

          {step === "quiz" && QUESTIONS[currentIdx] && (
            <FormSection>
              <div className="private-talent-pulse-form__quiz">
                <div className="private-talent-pulse-form__progress">
                  <div style={{ width: `${progress}%` }} />
                </div>

                <span className="private-talent-pulse-form__counter">
                  Question {currentIdx + 1} of {QUESTIONS.length}
                </span>

                <h3 className="private-talent-pulse-form__question">
                  {QUESTIONS[currentIdx].q}
                </h3>

                <div className="private-talent-pulse-form__options">
                  {QUESTIONS[currentIdx].options.map((option, index) => (
                    <Button
                      key={`${currentIdx}-${index}`}
                      value={option.text}
                      variant="primary-outlined"
                      disabled={saving}
                      onClick={() => handleAnswer(option.type)}
                    />
                  ))}
                </div>

                <div className="private-talent-pulse-form__actions">
                  {currentIdx > 0 && (
                    <Button
                      value="Previous"
                      icon={<ArrowLeft size={16} />}
                      variant="primary-outlined"
                      disabled={saving}
                      onClick={handlePrevious}
                    />
                  )}

                  <Button
                    value="Cancel"
                    variant="primary-outlined"
                    disabled={saving}
                    onClick={onClose}
                  />
                </div>
              </div>
            </FormSection>
          )}

          {step === "result" && result && (
            <FormSection>
              <div className="private-talent-pulse-form__result">
                <span className="private-talent-pulse-form__eyebrow">
                  {sport.toUpperCase()} PULSE
                </span>

                <h3>{result.title}</h3>

                {result.comparison && <p>{result.comparison}</p>}

                <div className="private-talent-pulse-form__strengths">
                  {(result.strengths || []).map((strength) => (
                    <span key={strength}>{strength}</span>
                  ))}
                </div>

                {result.note && <p>{result.note}</p>}

                <p className="private-talent-pulse-form__disclaimer">
                  Self-reported playing-style assessment. Results are
                  illustrative, not verified athletic measurements.
                </p>

                <div className="private-talent-pulse-form__actions">
                  <Button
                    value={saving ? "Saving..." : "Save to Talent ID"}
                    disabled={saving || loading}
                    onClick={handleSave}
                  />

                  <Button
                    value="Restart Test"
                    icon={<RotateCcw size={16} />}
                    variant="primary-outlined"
                    disabled={saving}
                    onClick={handleRestart}
                  />
                </div>
              </div>
            </FormSection>
          )}
        </FormLayout>
      </div>
    </FormModal>
  );
}
